import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment,
  Alert,
  CircularProgress,
  Stack,
} from '@mui/material';
import {
  Search,
  FilterList,
  Refresh,
  Payments,
  Store,
  Person,
  AccessTime,
  AccountBalanceWallet,
  Check,
  Close,
  Edit,
  Inbox,
} from '@mui/icons-material';
import { adminAPI } from '../../services/api';
import { useNotification } from '../../context/NotificationContext';

const STATUS_COLORS = {
  pending: 'warning',
  processing: 'info',
  completed: 'success',
  rejected: 'error',
};

const STATUS_FLOW = ['processing', 'completed', 'rejected'];

const Payouts = () => {
  const { showNotification } = useNotification();
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [updating, setUpdating] = useState(false);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminAPI.getPayoutRequests(
        statusFilter !== 'all' ? { status: statusFilter } : {}
      );
      const data = response.data || response || [];
      setRequests(data);
      setFilteredRequests(data);
    } catch (err) {
      console.error('Error fetching payout requests:', err);
      setError('Failed to load payout requests. Please try again.');
      showNotification('Failed to load payout requests', 'error');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, showNotification]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  useEffect(() => {
    let filtered = requests;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((r) => {
        const providerName = r.provider
          ? `${r.provider.firstName || ''} ${r.provider.lastName || ''}`.trim()
          : '';
        return (
          r.salon?.name?.toLowerCase().includes(term) ||
          providerName.toLowerCase().includes(term) ||
          r.upiId?.toLowerCase().includes(term)
        );
      });
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter((r) => r.status === statusFilter);
    }
    setFilteredRequests(filtered);
  }, [searchTerm, statusFilter, requests]);

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  const openDialog = (request) => {
    setSelected(request);
    setNewStatus(request.status === 'pending' ? '' : request.status);
    setAdminNote(request.adminNote || '');
    setDialogOpen(true);
  };

  const handleUpdateStatus = async () => {
    if (!newStatus) {
      showNotification('Please select a status', 'error');
      return;
    }
    setUpdating(true);
    try {
      await adminAPI.updatePayoutStatus(selected._id, {
        status: newStatus,
        adminNote: adminNote.trim(),
      });
      showNotification(`Payout request marked as ${newStatus}`, 'success');
      setDialogOpen(false);
      setSelected(null);
      setNewStatus('');
      setAdminNote('');
      fetchRequests();
    } catch (err) {
      console.error('Error updating payout status:', err);
      showNotification(
        err.response?.data?.message || 'Failed to update payout status',
        'error'
      );
    } finally {
      setUpdating(false);
    }
  };

  const getStats = () => {
    return {
      total: requests.length,
      pending: requests.filter((r) => r.status === 'pending').length,
      processing: requests.filter((r) => r.status === 'processing').length,
      completed: requests.filter((r) => r.status === 'completed').length,
      rejected: requests.filter((r) => r.status === 'rejected').length,
      totalAmount: requests
        .filter((r) => r.status === 'completed')
        .reduce((sum, r) => sum + (r.amount || 0), 0),
      pendingAmount: requests
        .filter((r) => r.status === 'pending' || r.status === 'processing')
        .reduce((sum, r) => sum + (r.amount || 0), 0),
    };
  };

  const formatProviderName = (provider) => {
    if (!provider) return 'N/A';
    return `${provider.firstName || ''} ${provider.lastName || ''}`.trim() || provider.email || 'N/A';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const stats = getStats();

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Payout Requests
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchRequests}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Pending
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {stats.pending}
                  </Typography>
                </Box>
                <AccessTime color="warning" fontSize="large" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Processing
                  </Typography>
                  <Typography variant="h4" color="info.main">
                    {stats.processing}
                  </Typography>
                </Box>
                <Payments color="info" fontSize="large" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Completed
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {stats.completed}
                  </Typography>
                </Box>
                <Check color="success" fontSize="large" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Pending Amount
                  </Typography>
                  <Typography variant="h4" color="primary.main">
                    ₹{stats.pendingAmount.toFixed(2)}
                  </Typography>
                </Box>
                <AccountBalanceWallet color="primary" fontSize="large" />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search by salon, provider or UPI ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="processing">Processing</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<FilterList />}
                onClick={handleClearFilters}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Payouts Table */}
      <Card>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Salon</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>UPI ID</TableCell>
                  <TableCell>Requested On</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                )}
                {!loading && filteredRequests.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <Stack alignItems="center" spacing={1} color="text.secondary">
                        <Inbox fontSize="large" />
                        <Typography>No payout requests found</Typography>
                      </Stack>
                    </TableCell>
                  </TableRow>
                )}
                {!loading &&
                  filteredRequests.map((request) => (
                    <TableRow key={request._id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Store fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                          <Typography variant="body2" fontWeight="medium">
                            {request.salon?.name || 'N/A'}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Person fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {formatProviderName(request.provider)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          ₹{(request.amount || 0).toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {request.upiId || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(request.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={request.status}
                          color={STATUS_COLORS[request.status] || 'default'}
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          onClick={() => openDialog(request)}
                          color="primary"
                          size="small"
                          title="Update status"
                        >
                          <Edit />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Update Status Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Update Payout Status</DialogTitle>
        <DialogContent>
          {selected && (
            <Box sx={{ mb: 2, mt: 1 }}>
              <Box display="flex" alignItems="center" mb={1}>
                <Store fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="subtitle1" fontWeight="bold">
                  {selected.salon?.name || 'N/A'}
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Provider
                  </Typography>
                  <Typography variant="body1">
                    {formatProviderName(selected.provider)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Amount
                  </Typography>
                  <Typography variant="body1" fontWeight="bold">
                    ₹{(selected.amount || 0).toFixed(2)}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    UPI ID
                  </Typography>
                  <Typography variant="body1">{selected.upiId}</Typography>
                </Grid>
                {selected.salon?.currentBalance !== undefined && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Current Salon Balance
                    </Typography>
                    <Typography variant="body1" color="primary" fontWeight="bold">
                      ₹{(selected.salon?.currentBalance || selected.currentBalance || 0).toFixed(2)}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}

          <FormControl fullWidth sx={{ mt: 1 }} required>
            <InputLabel>Status</InputLabel>
            <Select
              value={newStatus}
              label="Status *"
              onChange={(e) => setNewStatus(e.target.value)}
            >
              {STATUS_FLOW.map((status) => (
                <MenuItem key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Admin Note (optional)"
            placeholder="Add a note for the provider..."
            value={adminNote}
            onChange={(e) => setAdminNote(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDialogOpen(false);
              setSelected(null);
              setNewStatus('');
              setAdminNote('');
            }}
            startIcon={<Close />}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateStatus}
            color="primary"
            variant="contained"
            disabled={updating}
            startIcon={updating ? <CircularProgress size={16} /> : <Check />}
          >
            {updating ? 'Updating...' : 'Update Status'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Payouts;
